import { useState, useMemo, useEffect } from "react";
import { Button } from "@shared/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@shared/ui/card";
import { Plus, MapPin } from "lucide-react";
import { useToast } from "@shared/hooks/use-toast";
import { useSearchParams } from "react-router-dom";
import { ServiceCenterFormDialog } from "./serviceCenters/ServiceCenterForm";
import { ServiceCenterDetailView } from "./serviceCenters/ServiceCenterDetail";
import { ServiceCenterTable } from "./serviceCenters/ServiceCenterTable";
import {
  useDeleteServiceCenter,
  useServiceCenterList,
  useUpdateServiceCenterStatus,
  useDashboardDetails,
  useServiceCenters, // Add this import
} from "../hooks";
import { useUtilityServices } from "@features/mx/readCycleManagement/hooks";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";

// Define PAGE_SIZE constant
const PAGE_SIZE = 10;

interface ServiceCenter {
  id: string;
  name: string;
  address: string;
  city: string;
  cityCode: string;        // Add this
  phone: string;
  email: string;
  areas: string[];
  areaCodes: string[];     // Add this
  subAreas: string[];
  subAreaCodes: string[];  // Add this
  premises: string[];
  premiseCodes: string[];  // Add this
  services: string[];
  isActive: boolean;
  type: string;
  description: any;
}
export const ServiceCenterSettings = () => {
  const remoteUtilityId = getRemoteUtilityId();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  // Internal pagination state
  const [currentPage, setCurrentPage] = useState(1);

  // Read filters from URL (excluding page)
  const searchQuery = decodeURIComponent(searchParams.get("query") || "");
  const filterType = searchParams.get("type") || "";

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filterType]);

  // Build query parameters for API
  const queryParams = useMemo(
    () => ({
      remote_utility_id: remoteUtilityId,
      show_inactive: true,
      page: currentPage,
      limit: PAGE_SIZE,
      ...(searchQuery && { search_data: searchQuery }),
      ...(filterType && { type: filterType }),  
    }),
    [remoteUtilityId, currentPage, searchQuery, filterType]
  );

  // Dashboard details hook
  const dashboardDetailsParams = useMemo(() => ({
    remote_utility_id: remoteUtilityId,
  }), [remoteUtilityId]);

  // API hooks
  const { data, refetch } = useServiceCenterList(queryParams);
  const { refetch: refetchDashboardDetails } = useDashboardDetails(dashboardDetailsParams);

  // Add useServiceCenters hook to fetch service center types from API
  const { data: serviceCenterTypesData, isLoading: isServiceTypesLoading } = useServiceCenters({
    remote_utility_id: remoteUtilityId
  });

  const { data: utilitiesData } = useUtilityServices();
  const { mutate: updateServiceCenterStatus, isPending: isUpdatingStatus } =
    useUpdateServiceCenterStatus();
  const { mutate: deleteServiceCenter, isPending: isDeletingServiceCenter } =
    useDeleteServiceCenter();

  // Transform service center types from API for filter dropdown
  const serviceTypes = useMemo(() => {
    return serviceCenterTypesData?.result?.map(type => type.value || "") || [];
  }, [serviceCenterTypesData]);

  // Helper function to get user-friendly service labels from additional_data
  const getAdditionalServices = (additionalData: any): string[] => {
    if (!additionalData) return [];

    const serviceLabels: { [key: string]: string } = {
      isComplaints: "Complaints",
      isBillPayment: "Bill Payment",
      isDisconnection: "Disconnection",
      isNewConnections: "New Connections",
      isTechnicalSupport: "Technical Support",
      isDocumentCollection: "Document Collection",
    };

    return Object.entries(additionalData)
      .filter(([key, value]) => value === true && serviceLabels[key])
      .map(([key]) => serviceLabels[key]);
  };

  // Transform API data to match ServiceCenter interface
  const serviceCenters =
    data?.results?.map((item, index) => {
      // Get only additional services (from additional_data where value is true)
      const additionalServices = getAdditionalServices(item.additionalData);

      return {
        id: item.id?.toString() || "",
        name: item.name || "",
        address: item.address || "",
        city: Array.isArray(item.cityNames)
          ? item.cityNames.join(", ")
          : Array.isArray(item.cityNames)
          ? item.cityNames.join(", ")
          : "",
        cityCode: Array.isArray(item.city) ? item.city[0] : item.city || "",
        phone: item.contactNumber || "",
        email: item.email || "",
        areas: Array.isArray(item.areaNames) ? item.areaNames : Array.isArray(item.areaNames) ? item.areaNames : [],
        areaCodes: Array.isArray(item.area) ? item.area : [],
        subAreas: Array.isArray(item.subAreaNames) ? item.subAreaNames : Array.isArray(item.subAreaNames) ? item.subAreaNames : [],
        subAreaCodes: Array.isArray(item.subArea) ? item.subArea : [],
        premises: Array.isArray(item.premiseNames) ? item.premiseNames : Array.isArray(item.premiseNames) ? item.premiseNames : [],
        premiseCodes: Array.isArray(item.premise) ? item.premise : [],
        services: additionalServices,
        isActive: item.isActive ?? true,
        type: item.type || "",
        description: item.description || "",
      };
    }) || [];

  const [editingCenter, setEditingCenter] = useState<ServiceCenter | null>(
    null
  );
  const [viewingCenter, setViewingCenter] = useState<ServiceCenter | null>(
    null
  );
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);

  const handleSearch = (searchTerm: string, paramName: "search_data") => {
    const params = new URLSearchParams(searchParams);

    if (searchTerm.trim()) {
      params.set("query", searchTerm);
    } else {
      params.delete("query");
    }

    // Reset to page 1 when searching
    params.set("page", "1");
    setCurrentPage(1);

    setSearchParams(params);
  };

  const handleApplyFilters = (filters: { type?: string }) => {
    const params = new URLSearchParams(searchParams);

    // Clear existing filter params
    params.delete("type");
    params.delete("page");

    // Add new filter params
    if (filters.type) {
      params.set("type", filters.type);
    }

    // Reset to page 1 when applying filters
    params.set("page", "1");
    setCurrentPage(1);

    setSearchParams(params);
  };

  const handleResetFilters = () => {
    const params = new URLSearchParams(searchParams);

    // Remove all filter params
    params.delete("type");
    params.delete("page");
    params.delete("query");

    setCurrentPage(1);
    setSearchParams(params);
  };

  const handlePageChange = (page: number) => {
    const params = new URLSearchParams(searchParams);
    params.set("page", page.toString());
    setSearchParams(params);
    setCurrentPage(page);
  };

  const handleSaveCenter = (centerData: Partial<ServiceCenter>) => {
    if (editingCenter) {
      toast({
        title: "Service Center Updated",
        description: "The service center has been successfully updated.",
      });
    } else {
      // For new service centers, refetch the data
      refetch();
      toast({
        title: "Service Center Created",
        description: "A new service center has been successfully created.",
      });
    }
    setEditingCenter(null);
    setIsDialogOpen(false);
  };

  const handleDeleteCenter = (id: string) => {
    const payload = {
      id: id,
    };

    deleteServiceCenter(payload, {
      onSuccess: () => {
        refetch(); // Refetch service center data to update the UI
        refetchDashboardDetails(); // Refetch dashboard details after successful service center deletion
        toast({
          title: "Service Center Deleted",
          description: "The service center has been successfully deleted.",
        });
      },
      onError: (error) => {
        console.error("Failed to delete service center:", error);
        toast({
          title: "Error",
          description: "Failed to delete service center. Please try again.",
          variant: "destructive",
        });
      },
    });
  };

  const handleToggleActive = (center: ServiceCenter) => {
    const payload = {
      is_active: !center.isActive,
    };

    updateServiceCenterStatus(
      {
        id: center.id,
        payload,
      },
      {
        onSuccess: () => {
          refetch(); 
          refetchDashboardDetails(); 
          toast({
            title: "Status Updated",
            description: `Service center ${
              center.isActive ? "deactivated" : "activated"
            } successfully.`,
          });
        },
        onError: (error) => {
          console.error("Failed to update service center status:", error);
          toast({
            title: "Error",
            description:
              "Failed to update service center status. Please try again.",
            variant: "destructive",
          });
        },
      }
    );
  };

  const handleViewCenter = (center: ServiceCenter) => {
    console.log('Viewing center:', center);
    setViewingCenter(center);
    setIsViewDialogOpen(true);
  };

  const handleEditCenter = (center: ServiceCenter) => {
    setEditingCenter(center);
    setIsDialogOpen(true);
  };

  // Current filter values for the filter panel
  const currentFilters = useMemo(() => ({
    type: filterType,
  }), [filterType]);

  // Pagination object
  const pagination = data
    ? {
        currentPage,
        totalPages: Math.ceil(data.count / PAGE_SIZE),
        totalItems: data.count,
        startItem: (currentPage - 1) * PAGE_SIZE + 1,
        endItem: Math.min(currentPage * PAGE_SIZE, data.count),
        itemsPerPage: PAGE_SIZE,
      }
    : undefined;

  return (
    <div className="space-y-6">
      <Card>
<CardHeader className="mb-4">
 <div className="flex items-center justify-between">
   <div>
     <CardTitle className="flex items-center gap-2 page-subheading">
       <MapPin className="h-5 w-5" />
       Service Centers Management
     </CardTitle>
     <CardDescription className="page-subtitle">
       Configure service center locations, contact details, and operating
       hours for user reference.
     </CardDescription>
   </div>
   <Button
     onClick={() => {
       setEditingCenter(null);
       setIsDialogOpen(true);
     }}
   >
     <Plus className="h-4 w-4 mr-2" />
     Add Service Center
   </Button>
 </div>
</CardHeader>
        <CardContent className="space-y-4 mt-4">
          <ServiceCenterTable
            serviceCenters={serviceCenters}
            onView={handleViewCenter}
            onEdit={handleEditCenter}
            onDelete={handleDeleteCenter}
            onToggleActive={handleToggleActive}
            isUpdatingStatus={isUpdatingStatus}
            searchPlaceholder="Search service centers by name, location, or contact..."
            searchParamName="query"
            searchDebounceMs={300}
            onSearch={handleSearch}
            currentFilters={currentFilters}
            onApplyFilters={handleApplyFilters}
            onResetFilters={handleResetFilters}
            serviceTypes={serviceTypes} // Now using API data instead of hardcoded array
            pagination={pagination}
            onPageChange={handlePageChange}
          />
        </CardContent>
      </Card>

      {isDialogOpen && (
        <ServiceCenterFormDialog
          center={editingCenter}
          serviceTypes={[]} // No longer needed, using API data
          availableServices={[]} // No longer needed, using predefined services
          onSave={handleSaveCenter}
          onRefetch={refetch}
          onCancel={() => {
            setEditingCenter(null);
            setIsDialogOpen(false);
          }}
        />
      )}

      <ServiceCenterDetailView
        center={viewingCenter}
        isOpen={isViewDialogOpen}
        onClose={() => setIsViewDialogOpen(false)}
      />
    </div>
  );
};